import * as Api from '@ViewModels';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const INFINITE_CUSTOM_SURVEYS_QUERY_KEY = 'survey/custom';

export function useInfiniteCustomSurveys({
	shouldIncludeArchived = false,
	shouldIncludeExpired = false,
	search = '',
	pageSize = 100,
	enabled = true,
	impersonationContext,
}: {
	shouldIncludeArchived?: boolean;
	shouldIncludeExpired?: boolean;
	search?: string;
	pageSize?: number;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	transformer?: (page: Api.IPagedCollection<Api.ICustomSurvey>) => Api.IPagedCollection<Api.ICustomSurvey>;
}) {
	const userSession = useUserSession();
	return useInfiniteQuery<Api.IPagedCollection<Api.ICustomSurvey>>({
		enabled,
		getNextPageParam: lastPage => lastPage.pageToken,
		queryFn: async ({ pageParam }) => {
			const page = await userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.ICustomSurvey>>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams: {
						includeArchived: shouldIncludeArchived,
						includeExpired: shouldIncludeExpired,
						search,
						pageSize,
						pageToken: pageParam,
					},
					urlPath: `survey/custom`,
				}),
				'GET',
				null
			);

			return page;
		},
		queryKey: [
			INFINITE_CUSTOM_SURVEYS_QUERY_KEY,
			shouldIncludeArchived,
			shouldIncludeExpired,
			search,
			pageSize,
			enabled,
			impersonationContext,
		],
		initialPageParam: '',
	});
}

export const invalidateInfiniteCustomSurveys = () => {
	reactQueryClient.invalidateQueries({ queryKey: [INFINITE_CUSTOM_SURVEYS_QUERY_KEY] });
};
